import { FieldValue, doc, getDoc, increment } from 'firebase/firestore';
import { uniq } from 'lodash';
import uniqid from 'uniqid';
import {
  ClientCafe,
  Dish,
  LegalEntity,
  Order,
  OrderLog,
  OrderStatus,
  Paid,
  PosShift,
  PromotionApplied,
  PrroReceipt,
  Store,
  StoreLog,
  Wallet,
  WalletLog,
  Worker,
} from '../../apiTypes';
import TransactionCategories from '../../constants/transactionCategories';
import { db } from '../../firebase';
import { createBatch } from '../../utils/api';
import { canClose } from '../../utils/canCloseOrder';
import getBankCommission from '../../utils/getBankCommission';
import { BillCalc, getItemsByLegalEntityIds } from '../../utils/getBillCalc';
import { isNotNull } from '../../utils/isNotNull';
import { Map } from '../../utils/map';
import { PrroItem, getPrroDiscounts, getPrroItems } from '../../utils/prro';

type Props = {
  cafeSlug: string;
  order: DbDocument<Order>;
  billCalc: BillCalc;
  worker?: DbDocument<Worker>;
  method: 'cash' | 'card';
  discount: number;
  service: number;
  wallets: DbDocument<Wallet>[];
  currentPosShift?: DbDocument<PosShift>;
  dishesMap: Map<DbDocument<Dish>>;
  shouldSpendProducts?: boolean;
  promotionsApplied: PromotionApplied[];
  legalEntitiesMap: Map<DbDocument<LegalEntity>>;
  defaultLegalEntityId?: string;
};

export const payOrder = async ({
  order,
  billCalc,
  worker,
  cafeSlug,
  method,
  wallets = [],
  discount,
  service,
  currentPosShift,
  dishesMap,
  shouldSpendProducts,
  promotionsApplied,
  legalEntitiesMap,
  defaultLegalEntityId,
}: Props) => {
  const errors: string[] = [];
  const prroReceipts: Record<string, PrroReceipt> = {};
  const itemsByLegalEntityIds = getItemsByLegalEntityIds(Object.values(order.items), defaultLegalEntityId);

  Object.keys(itemsByLegalEntityIds).forEach((legalEntityId) => {
    const legalEntity = legalEntitiesMap[legalEntityId];
    if (legalEntity?.prroCheckBox.enabled) {
      prroReceipts[legalEntityId] = {
        provider: 'checkbox',
        id: null,
        status: 'waiting',
        legalEntityId,
      };
    }
  });

  const promises = Object.entries(itemsByLegalEntityIds).map(async ([legalEntityId, orderItems]) => {
    const legalEntity = legalEntitiesMap[legalEntityId];

    if (legalEntity?.prroCheckBox.automatic && legalEntity?.prroCheckBox.key && currentPosShift && dishesMap) {
      const prroItems: PrroItem[] = getPrroItems(orderItems, dishesMap);
      const discounts = getPrroDiscounts(legalEntityId, billCalc, discount, service);
      const payments = [
        method === 'card' ? { type: 'CARD', label: 'Картка', value: 0 } : { type: 'CASH', label: 'Готівка', value: 0 },
      ];

      const res = await fetch('https://api.checkbox.in.ua/api/v1/receipts/sell', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${legalEntity.prroCheckBox.cashierToken}`,
          'X-Client-Name': 'FireMenu',
          'X-Client-Version': 'v0.1.1',
        },
        body: JSON.stringify({
          goods: prroItems,
          payments,
          rounding: true,
          footer: null,
          discounts,
        }),
      });

      const resJson = await res.json();

      if (res.status !== 201) {
        errors.push(resJson.message as string);
      }

      const prroReceipt = prroReceipts[legalEntityId];
      if (resJson.id && prroReceipt) {
        prroReceipt.id = resJson.id;
      }
    }
  });

  await Promise.all(promises);

  if (errors.length > 0) {
    return { errors };
  }

  const batch = createBatch();

  if (order.clientId) {
    try {
      const docRef = doc(db, 'cafes', cafeSlug, 'clients', order.clientId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        batch.update<ClientCafe>(`cafes/${cafeSlug}/clients/${order.clientId}`, {
          totalSum: increment(billCalc.total.toPay),
        });
      }
    } finally {
      /** skip */
    }
  }

  const paid: Paid = {
    cash: Object.fromEntries(
      Object.entries(billCalc.byLegalEntity).map(([key, bill]) => [key, method === 'cash' ? bill.toPay : 0])
    ),
    byCard: Object.fromEntries(
      Object.entries(billCalc.byLegalEntity).map(([key, bill]) => [key, method === 'card' ? bill.toPay : 0])
    ),
    total: billCalc.total.toPay,
    discount,
    service,
    promotionsApplied,
  };

  const status: OrderStatus | null = order.type !== 'delivery' ? 'complete' : order.status || null;

  Object.entries(billCalc.byLegalEntity).forEach(([legalEntityId, bill]) => {
    const wallet =
      wallets.find(
        (w) => w.legalEntityId === legalEntityId && (method === 'card' ? w.forCardPayments : w.forCashPayments)
      ) || wallets.find((w) => !w.legalEntityId && (method === 'card' ? w.forCardPayments : w.forCashPayments));

    const bankCommission = getBankCommission(bill.toPay, wallet?.bankPercent);

    if (wallet) {
      const log: WalletLog = {
        time: new Date().getTime(),
        sum: bill.toPay,
        workerId: worker?.id || '',
        workerName: worker?.name || 'Visitor',
        wallet: wallet.name,
        bankCommission,
        category: order.type === 'delivery' ? TransactionCategories.delivery : TransactionCategories.in_house_sales,
        total: (wallet.balance || 0) + bill.toPay - bankCommission,
        info: { type: 'order', id: order.id, number: order.number },
        type: 'order',
        comment: '',
      };
      batch.update<Wallet>(`cafes/${cafeSlug}/wallets/${wallet.id}`, {
        balance: increment(bill.toPay - bankCommission),
      });
      batch.set<WalletLog>(`cafes/${cafeSlug}/wallets/${wallet.id}/logs/${uniqid()}`, log);
    }
  });

  const orderLog: OrderLog = {
    time: Date.now(),
    workerId: worker?.id || '',
    workerName: worker?.name || 'Visitor',
    type: 'pay',
    state: {
      workerId: order.workerId,
      workerName: order.workerName,
      tableId: order.tableId,
      tableName: order.tableName,
      items: order.items,
      clientId: order.clientId || '',
      clientName: order.clientName || '',
      discount: order.discount || 0,
      status,
      paid,
      delivery: order.delivery || null,
    },
  };

  batch.update<Order>(`cafes/${cafeSlug}/orders/${order.id}`, {
    paid,
    paidTime: Date.now(),
    prroReceipts: prroReceipts as unknown as FieldValue,
    status,
    closed: canClose({ ...order, paid, status, prroReceipts }),
  });

  batch.set<OrderLog>(`cafes/${cafeSlug}/orders/${order.id}/logs/${uniqid()}`, orderLog);

  if (shouldSpendProducts) {
    const storesToUpdate = uniq(
      Object.values(order.items)
        .map((item) => dishesMap[item.dishId]?.components.map((c) => c.store) || [])
        .flat()
    ).filter(isNotNull);

    storesToUpdate.forEach((s) => {
      const diff: Record<string, number> = {};
      const updates: Record<`residues.${string}`, FieldValue> = {};

      Object.values(order.items).forEach((item) => {
        dishesMap[item.dishId]?.components.forEach((c) => {
          if (c.store === s) {
            diff[c.id] = (diff[c.id] || 0) - c.qnt;
          }
        });
      });

      Object.entries(diff).forEach(([id, qnt]) => {
        updates[`residues.${id}`] = increment(qnt);
      });

      batch.update<Store>(`cafes/${cafeSlug}/stores/${s}`, updates);

      batch.set<StoreLog>(`cafes/${cafeSlug}/storeLogs/${uniqid()}`, {
        time: Date.now(),
        workerId: '',
        workerName: 'Visitor',
        storeId: s,
        type: 'selfOrder',
        diff,
        info: {
          type: 'selfOrder',
          number: order.number,
          id: order.id,
        },
      });
    });
  }

  await batch.commit();

  return { paid, errors };
};
